import dayjs from 'dayjs'
import _ from 'lodash'

// Logic ghép unit/gom nhóm DÙNG CHUNG giữa 2 renderer của Cây Gia Phả (CSS ul/li - FamilyBooks.js và
// SVG toạ độ - SvgPedigreeTree.js) + Tâm Điểm (FamilyBooks.js). Tách riêng ra đây (không định nghĩa
// lặp lại ở từng renderer) để 2 cách vẽ luôn dùng ĐÚNG 1 nguồn sự thật cho "unit là gì, gom nhóm ra
// sao" - tránh 2 renderer lệch nhau khi 1 bên sửa mà quên sửa bên kia.

// Giới hạn độ sâu tối đa - cây phả hệ có thể sâu vô hạn về lý thuyết, giới hạn để tránh hiệu năng kém
// khi tìm nhánh quá dài trên mobile; nhánh bị cắt do chạm giới hạn báo bằng 1 chấm nhỏ dưới ô (không
// phải trang trí - báo hiệu còn dữ liệu chưa hiện, bấm vào tên vẫn xem chi tiết được).
export const PEDIGREE_MAX_DEPTH = 8

// Ghép vợ/chồng (spouse_dedup_keys) thành "unit" liền kề rồi sắp các unit theo năm sinh sớm nhất
// trong unit đó (con cái theo đúng thứ tự năm sinh, vợ/chồng luôn đứng cạnh nhau) - dùng chung giữa
// sub-tab Thành Viên (flatten cả rừng, không chia thế hệ) và Cây Gia Phả (giữ nguyên unit theo từng
// thế hệ để vẽ đường nối vợ/chồng). `visibleSet` optional - lọc phạm vi đang xem (drill-down cây).
//
// 1 người có THỂ có NHIỀU vợ/chồng (spouse_dedup_keys là mảng - mỗi cuộc hôn nhân 1 sổ độc lập, xem
// giải thích ở BE getMyFamilyForests()). Mặc định (`mergeSpouses` = false) SINH NHIỀU UNIT riêng (1
// unit/1 cuộc hôn nhân) - dùng cho Cây Gia Phả (PedigreeNode), nơi có cấu trúc phân nhánh trực quan
// nên tách khối riêng biệt hợp lý (đúng cách sổ gia đình giấy vẫn trình bày mỗi cuộc hôn nhân 1
// dòng/1 sổ). `mergeSpouses` = true dùng cho khu vực LIỆT KÊ PHẲNG không có phân nhánh (TierGroup ở
// Tâm Điểm) - lặp cùng 1 tên ở đó trông giống trùng lặp/bug hơn là 2 nhánh riêng, nên gộp về ĐÚNG 1
// unit/người, unit dạng [primary, ...tất cả vợ/chồng] (UnitCard tự nhận diện length>2 để hiện gọn).
export const buildMemberUnits = (members, byKey, visibleSet, mergeSpouses = false) => {
    // "đã làm vai trò vợ/chồng ở unit khác" - KHÔNG tự làm primary/unit riêng nữa (tránh render lặp
    // theo chiều ngược), nhưng KHÔNG chặn 1 người làm primary ở NHIỀU unit (multi-spouse, chỉ khi
    // !mergeSpouses).
    const pairedAsSpouse = new Set()
    const units = []
    members.forEach((m) => {
        if (visibleSet && !visibleSet.has(m.dedup_key)) return
        if (pairedAsSpouse.has(m.dedup_key)) return
        const spouseKeys = (m.spouse_dedup_keys || []).filter((sk) => (
            sk && byKey[sk] && (!visibleSet || visibleSet.has(sk)) && !pairedAsSpouse.has(sk) && sk !== m.dedup_key
        ))
        if (!spouseKeys.length) {
            units.push([m]);
            return
        }
        if (mergeSpouses) {
            spouseKeys.forEach((sk) => pairedAsSpouse.add(sk))
            units.push([m, ...spouseKeys.map((sk) => byKey[sk])])
            return
        }
        spouseKeys.forEach((sk) => {
            pairedAsSpouse.add(sk)
            units.push([m, byKey[sk]])
        })
    })
    const earliestBirth = (unit) => {
        const dates = unit.map((p) => p.birth_date).filter(Boolean).map((d) => dayjs(d).valueOf())
        return dates.length ? Math.min(...dates) : Infinity
    }
    // Gom các unit CÙNG primary (multi-spouse, !mergeSpouses) đứng LIỀN KỀ nhau - sort thẳng theo
    // năm sinh từng unit có thể bị 1 anh/chị/em khác chen vào giữa 2 cuộc hôn nhân của CÙNG 1 người.
    // Đứng liền kề để groupUnitsForRender() gom lại bọc chung 1 khung (MultiMarriageGroup).
    // Nhóm theo primary TRƯỚC, rồi mới sắp XẾP CÁC NHÓM theo năm sinh sớm nhất trong nhóm - thứ tự
    // vợ/chồng trong 1 nhóm giữ nguyên như lúc push (theo spouse_dedup_keys).
    const groups = []
    const groupIndexByPrimaryKey = {}
    units.forEach((unit) => {
        const primaryKey = unit[0].dedup_key
        if (groupIndexByPrimaryKey[primaryKey] === undefined) {
            groupIndexByPrimaryKey[primaryKey] = groups.length
            groups.push([unit])
        } else {
            groups[groupIndexByPrimaryKey[primaryKey]].push(unit)
        }
    })
    groups.sort((a, b) => Math.min(...a.map(earliestBirth)) - Math.min(...b.map(earliestBirth)))
    return groups.flat()
}

// Key ổn định cho 1 "cặp" (không phân biệt thứ tự) - dùng để tra cứu con cái ĐÚNG theo từng cặp
// riêng (xem childrenByCouple) thay vì gộp lẫn con của MỌI cuộc hôn nhân vào 1 rổ chung.
export const coupleKeyOf = (unit) => unit.map((p) => p.dedup_key).sort().join('|')

// Gốc THẬT của 1 rừng - ĐỌC TRỰC TIẾP field `is_root` do BACKEND tính sẵn (FamilyBookController::
// markRootMembers() - bounded walk qua effective_parent_dedup_keys, có cap độ sâu (config
// family_books.max_ancestor_visibility_depth) để không treo/tràn stack với dữ liệu cực sâu, và loại
// trừ đúng vợ/chồng "married-in" của người có cha/mẹ) - KHÔNG tự suy lại ở client (quyết định cùng
// user 2026-08-13: "tính root từ backend, client chỉ hiển thị" - tránh 2 nguồn tính khác nhau, tránh
// FE phải tự lo phòng vòng lặp/đệ quy sâu cho dữ liệu cực lớn).
//
// DÙNG CHUNG cho: (1) FullPedigreeModal dựng gốc cây, (2) ForestSelector đặt tên nút chọn rừng,
// (3) Tâm Điểm chọn tâm điểm mặc định - TRƯỚC ĐÂY (2)/(3) mỗi nơi tự suy 1 kiểu KHÁC NHAU (label lấy
// forest.books[0].title, tâm điểm lấy forest.books[0].head_member - thứ tự mảng books từ BE không hề
// phản ánh ai là gốc thật) - sai ngay khi rừng nhiều sổ và gốc lại là người "lưng chừng" bất kỳ (bug
// thực tế đã gặp). Giờ cả 3 nơi dùng ĐÚNG 1 nguồn, trả về mảng unit đã sắp theo năm sinh (buildMemberUnits).
export const findRootUnits = (members, byKey) => {
    // Tương thích ngược - BE CHƯA deploy field is_root (đang rollout dở BE/FE lệch nhau) thì response
    // hoàn toàn không có field này ở BẤT KỲ member nào - tự suy lại bằng ĐÚNG công thức cũ (không cha/
    // mẹ + không phải married-in spouse) thay vì lọc ra rỗng rồi rơi về books[0] (sai hẳn - bug cũ đã
    // gặp). CÓ field is_root (dù false) -> tin TUYỆT ĐỐI BE, không tự suy lại (BE đã tính bounded-depth
    // đúng, xem markRootMembers()) - chỉ dùng công thức cũ làm lưới an toàn tạm thời lúc rollout.
    const hasIsRootField = members.some((m) => m.is_root !== undefined)
    if (hasIsRootField) {
        return buildMemberUnits(members.filter((m) => m.is_root), byKey)
    }
    const hasKnownParent = (m) => (m.effective_parent_dedup_keys || []).some((k) => byKey[k])
    const isMarriedInSpouse = (m) => (m.spouse_dedup_keys || []).some((sk) => hasKnownParent(byKey[sk] || {}))
    const rootCandidates = members.filter((m) => !hasKnownParent(m) && !isMarriedInSpouse(m))
    return buildMemberUnits(rootCandidates, byKey)
}

// Tên "neo" CÁ NHÂN HOÁ theo người xem - cha/mẹ (chồng/vợ) TRỰC TIẾP của viewer (đọc field `is_viewer`
// do BE trả riêng cho người đang đăng nhập, xem FamilyBookController::getMyFamilyForests()) - dùng để
// gợi ý thêm lên chip chọn cây khi phải leo nhiều đời mới tới gốc (vd chip "LÊ Túc & LÊ Thị Hoán +33"
// không tự nói lên gì, nhưng biết nhánh này dẫn xuống "Lê Viết Dũng" - cha/mẹ chồng/vợ mà viewer chắc
// chắn biết mặt - thì hiểu ngay đây là bên nào, không cần leo tay từng đời để dò). Quyết định cùng
// user 2026-08-17: MỖI NGƯỜI XEM thấy neo KHÁC NHAU (con thấy tên cha/mẹ mình, cháu họ xem cùng cây
// thấy tên cha/mẹ CỦA HỌ) - không phải 1 nhãn cố định chung cho mọi người, nên tính lại theo `is_viewer`
// mỗi lần render, KHÔNG cache/lưu cứng.
//
// `forests` = TOÀN BỘ mảng forests (không chỉ forest đang chọn) - viewer hoặc chính cha/mẹ họ có thể
// nằm ở 1 forest KHÁC forest đang xét (dedup_key ổn định xuyên forest vì lấy từ family_person_id, xem
// BE), nên gộp members của MỌI forest lại 1 lần trước khi tra cứu, không giới hạn theo forest hiện tại.
export const findViewerAnchorNames = (forests) => {
    const allMembers = forests.flatMap((f) => f.members || [])
    const byKey = _.keyBy(allMembers, 'dedup_key')
    const viewer = allMembers.find((m) => m.is_viewer)
    if (!viewer) return []
    // Viewer là DÂU/RỂ (married-in) - cha/mẹ RUỘT của họ ở 1 gia đình hoàn toàn khác, không nằm trong
    // phạm vi cây này nên effective_parent_dedup_keys rỗng - phải LẤY THEO VỢ/CHỒNG (spouse_dedup_keys)
    // thay vào, đúng vị trí họ đang đứng trong CHÍNH gia đình này (vd Trúc không có cha/mẹ ruột ghi
    // nhận ở đây, phải lấy cha/mẹ của chồng - Bình Linh - làm neo, chứ không phải bỏ trống). Bug thực
    // tế đã gặp: account Bình Linh (máu mủ, có effective_parent_dedup_keys) hiện đúng neo, account
    // Trúc (dâu) hoàn toàn không hiện neo nào.
    const anchorSource = (viewer.effective_parent_dedup_keys || []).length
        ? viewer
        : (viewer.spouse_dedup_keys || []).map((k) => byKey[k]).filter(Boolean)[0]
    if (!anchorSource) return []
    return (anchorSource.effective_parent_dedup_keys || []).map((k) => byKey[k]).filter(Boolean)
}

// Gom TẤT CẢ unit cùng chung 1 primary (multi-spouse) thành 1 NHÓM RENDER duy nhất - đã thử đánh
// badge số thứ tự (1/2, 2/2) lên 2 box tách rời, KHÔNG đủ thuyết phục ("như 2 anh em sinh đôi trùng
// tên", phản hồi thực tế); rồi thử vẽ 1 box primary toả xuống nhiều nhánh hôn nhân, cũng KHÔNG ổn
// (đường nối xuống giống hệt quan hệ cha-con, bị hiểu nhầm "vợ là con"). Nhóm >1 phần tử giờ bọc
// KHUNG chung quanh các unit gốc (MultiMarriageGroup - không đổi cấu trúc quan hệ đang vẽ, chỉ thêm
// viền + nhãn tên 1 lần).
//
// Gom theo BẢN ĐỒ (key -> vị trí nhóm), KHÔNG đòi hỏi các unit cùng primary phải LIỀN KỀ sẵn trong
// mảng đầu vào - phản hồi thực tế đã gặp: anh/chị/em ruột khác (Hai, Lục) chen được vào GIỮA 2 cụm
// vợ của cùng 1 người (Phần) khi chỉ gom theo kề nhau (phụ thuộc hoàn toàn vào buildMemberUnits đã
// sắp đúng thứ tự chưa - dễ vỡ khi có thêm anh/chị/em khác trộn vào cùng danh sách). Gom theo bản đồ
// đảm bảo ĐÚNG bất kể thứ tự mảng đầu vào ra sao.
export const groupUnitsForRender = (units) => {
    const groups = []
    const groupIndexByPrimaryKey = {}
    units.forEach((unit) => {
        const primaryKey = unit[0].dedup_key
        if (groupIndexByPrimaryKey[primaryKey] === undefined) {
            groupIndexByPrimaryKey[primaryKey] = groups.length
            groups.push([unit])
        } else {
            groups[groupIndexByPrimaryKey[primaryKey]].push(unit)
        }
    })
    return groups
}

// Dựng CÂY DỮ LIỆU thuần (không phải JSX) cho 1 nhánh phả hệ, bắt đầu từ 1 group (mảng unit cùng
// primary, lấy từ groupUnitsForRender) - DÙNG CHUNG cho renderer SVG (SvgPedigreeTree, cần toạ độ nên
// phải có cây dữ liệu để đo trước khi vẽ) và có thể dùng lại cho renderer CSS sau này nếu cần. Mô
// phỏng ĐÚNG logic đệ quy của PedigreeNode/MultiMarriageGroup (JSX) ở FamilyBooks.js - chỉ khác là trả
// về data thay vì JSX. `ctx` = {byKey, childrenByEffectiveParent, childrenByCouple, visited (Set,
// MUTATE trực tiếp như PedigreeNode - guard chống lặp vô hạn/hiện trùng, tạo mới mỗi lần render, xem
// lý do ở FullPedigreeModal)}.
//
// Node trả về:
//   kind 'single': {kind, key, primary, spouse (hoặc null), truncated, duplicate, children: node[]}
//   kind 'multi' (units.length > 1, multi-spouse): {kind, key, primary, branches: [{spouse, truncated, duplicate, children: node[]}]}
//
// `duplicate`: cặp vợ/chồng này nối 2 dòng họ khác nhau (mỗi bên có cha/mẹ ở 1 nhánh riêng) - VỀ BẢN
// CHẤT là 1 đồ thị, không phải cây, nên chỉ đệ quy con cháu được ở ĐÚNG 1 nhánh (nhánh build TỚI
// TRƯỚC). Trước đây nhánh tới SAU return null thẳng - cặp biến mất KHÔNG DẤU VẾT (bug thực tế đã
// gặp). Giờ vẫn trả node đầy đủ (renderer vẽ được cả 2 ô) nhưng children luôn rỗng + duplicate=true
// để renderer tự thêm nhãn phân biệt (đồng bộ ĐÚNG cách PedigreeNode ở FamilyBooks.js đang xử lý -
// xem comment ở đó, không tự chế hành vi khác cho renderer SVG này).
export const buildPedigreeDataNode = (group, depth, ctx) => {
    if (!group || !group.length) return null
    if (group.length === 1) {
        const unit = group[0]
        const [primary, spouse] = unit
        const unitKey = coupleKeyOf(unit)
        if (!primary) return null
        const isDuplicate = ctx.visited.has(unitKey)
        ctx.visited.add(unitKey)
        const atLimit = depth >= PEDIGREE_MAX_DEPTH
        const childKeys = isDuplicate ? new Set() : new Set(
            spouse ? (ctx.childrenByCouple[unitKey] || []) : (ctx.childrenByEffectiveParent[primary.dedup_key] || [])
        )
        const childUnits = (!atLimit && childKeys.size)
            ? buildMemberUnits(Array.from(childKeys).map((k) => ctx.byKey[k]).filter(Boolean), ctx.byKey)
            : []
        const children = groupUnitsForRender(childUnits).map((g) => buildPedigreeDataNode(g, depth + 1, ctx)).filter(Boolean)
        return {kind: 'single', key: unitKey, primary, spouse: spouse || null, truncated: atLimit && childKeys.size > 0, duplicate: isDuplicate, children}
    }
    const primary = group[0][0]
    const branches = group.map((unit) => {
        const [, spouse] = unit
        const unitKey = coupleKeyOf(unit)
        if (!spouse) return null
        const isDuplicate = ctx.visited.has(unitKey)
        ctx.visited.add(unitKey)
        const atLimit = depth >= PEDIGREE_MAX_DEPTH
        const childKeys = isDuplicate ? new Set() : new Set(ctx.childrenByCouple[unitKey] || [])
        const childUnits = (!atLimit && childKeys.size)
            ? buildMemberUnits(Array.from(childKeys).map((k) => ctx.byKey[k]).filter(Boolean), ctx.byKey)
            : []
        const children = groupUnitsForRender(childUnits).map((g) => buildPedigreeDataNode(g, depth + 1, ctx)).filter(Boolean)
        return {spouse, truncated: atLimit && childKeys.size > 0, duplicate: isDuplicate, children}
    }).filter(Boolean)
    if (!branches.length) return null
    return {kind: 'multi', key: `${primary.dedup_key}_multi`, primary, branches}
}
